/**
 *  PDP — gallery thumbnails + variant sync
 */
(function () {
  const VARIANT_UPDATE = "variant:update";

  const getMainImage = (root) =>
    root.querySelector(
      ".pdp__main-img, [data-main-image], .pdp__main-media img",
    );

  const setActiveThumb = (root, mediaId) => {
    if (!mediaId) return;
    const id = String(mediaId);
    root.querySelectorAll("[data-thumb]").forEach((thumb) => {
      const active = String(thumb.dataset.mediaId) === id;
      thumb.classList.toggle("is-active", active);
      thumb.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const setActiveThumbBySrc = (root, src) => {
    if (!src) return;
    const normalizedSrc = String(src).split("?")[0];
    root.querySelectorAll("[data-thumb]").forEach((thumb) => {
      const thumbSrc = String(thumb.dataset.fullSrc || "").split("?")[0];
      const active = thumbSrc === normalizedSrc;
      thumb.classList.toggle("is-active", active);
      thumb.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const setMainImage = (root, { src, alt, mediaId }) => {
    const img = getMainImage(root);
    if (!img || !src) return;

    // Clear srcset so the browser is forced to use the new src
    img.removeAttribute("srcset");
    img.src = src;
    if (alt) img.alt = alt;
    if (mediaId) img.dataset.mediaId = String(mediaId);
  };

  const selectThumb = (root, thumb) => {
    const src = thumb.dataset.fullSrc;
    if (!src) return;

    setMainImage(root, {
      src,
      alt: thumb.dataset.alt,
      mediaId: thumb.dataset.mediaId,
    });
    setActiveThumb(root, thumb.dataset.mediaId);
  };

  const getSelectedColor = (root) => {
    const picker = root.querySelector("variant-picker");
    const checked = picker?.querySelector(
      '.pdp__swatches input[type="radio"]:checked',
    );
    return checked ? String(checked.value).trim().toLowerCase() : "";
  };

  const setClearButton = (root, visible) => {
    const clearBtn = root.querySelector("[data-gallery-clear]");
    if (clearBtn) clearBtn.hidden = !visible;
  };

  const showAllThumbs = (root) => {
    root
      .querySelectorAll("[data-thumb]")
      .forEach((t) => t.classList.remove("pdp__thumb--hidden"));
  };

  // Show only thumbnails that belong to the selected color. Images with no
  // color association (empty data-colors) are treated as shared and always
  // shown. If no color data is present at all, nothing is hidden.
  // Filtering is off by default and only turns on when the shopper picks a
  // color; the "Show all" button turns it back off.
  const applyColorFilterInner = (root) => {
    const thumbsWrap = root.querySelector("[data-pdp-thumbs]");
    if (!thumbsWrap || thumbsWrap.dataset.colorFilter !== "true") {
      setClearButton(root, false);
      return;
    }

    const thumbs = Array.from(thumbsWrap.querySelectorAll("[data-thumb]"));
    const color = getSelectedColor(root);
    const hasColorData = thumbs.some((t) => t.dataset.colors);

    // By default all images are shown. Filtering only kicks in once the shopper
    // actively picks a color. The active flag lives on the persistent [data-pdp]
    // root so it survives gallery/variant-picker re-rendering.
    const active = root.dataset.colorFilterActive === "true";

    if (!active || !color || !hasColorData) {
      showAllThumbs(root);
      setClearButton(root, false);
      return;
    }

    // In strict mode, images with no color link are hidden while a color is
    // selected. Otherwise they are treated as shared and stay visible.
    const strict = thumbsWrap.dataset.colorFilterStrict === "true";
    const needle = `|${color}|`;

    const matches = thumbs.map((thumb) => {
      const colors = thumb.dataset.colors || "";
      return colors === "" ? !strict : colors.includes(needle);
    });

    // Safety net: never leave the gallery empty. If nothing matches the
    // selected color (e.g. no image is tagged for it), show everything.
    if (!matches.some(Boolean)) {
      showAllThumbs(root);
      setClearButton(root, false);
      return;
    }

    let firstVisible = null;
    let activeVisible = false;
    let hiddenCount = 0;

    thumbs.forEach((thumb, i) => {
      const match = matches[i];
      thumb.classList.toggle("pdp__thumb--hidden", !match);
      if (match) {
        if (!firstVisible) firstVisible = thumb;
        if (thumb.classList.contains("is-active")) activeVisible = true;
      } else {
        hiddenCount += 1;
      }
    });

    // Only offer the "clear" button when the filter actually hid something.
    setClearButton(root, hiddenCount > 0);

    // If the current main image is not part of the selected color, jump to the
    // first image of that color.
    if (firstVisible && !activeVisible) {
      selectThumb(root, firstVisible);
    }
  };

  // The thumbnail strip shows a single horizontal line of ~4 thumbs. The track
  // is translated with transform (not native scrolling) so paging is reliable
  // regardless of overflow quirks. The prev/next arrows page by a whole number
  // of thumbs; they hide when everything already fits and are disabled at each
  // end of the range.
  const getThumbsMax = (track, viewport) =>
    Math.max(0, track.scrollWidth - viewport.clientWidth);

  const getThumbsOffset = (track) =>
    parseFloat(track.dataset.thumbsOffset || "0") || 0;

  const setThumbsOffset = (root, offset) => {
    const track = root.querySelector("[data-pdp-thumbs]");
    const viewport = root.querySelector("[data-pdp-thumbs-viewport]");
    if (!track || !viewport) return;
    const max = getThumbsMax(track, viewport);
    const clamped = Math.min(Math.max(offset, 0), max);
    track.dataset.thumbsOffset = String(clamped);
    track.style.transform = `translateX(${-clamped}px)`;
    updateThumbsArrows(root);
  };

  const updateThumbsArrows = (root) => {
    const track = root.querySelector("[data-pdp-thumbs]");
    const viewport = root.querySelector("[data-pdp-thumbs-viewport]");
    const wrap = root.querySelector("[data-pdp-thumbs-wrap]");
    if (!track || !viewport || !wrap) return;
    const prev = wrap.querySelector('[data-thumbs-arrow="prev"]');
    const next = wrap.querySelector('[data-thumbs-arrow="next"]');
    const max = getThumbsMax(track, viewport);
    if (max <= 1) {
      prev?.setAttribute("hidden", "");
      next?.setAttribute("hidden", "");
      return;
    }
    prev?.removeAttribute("hidden");
    next?.removeAttribute("hidden");
    const offset = getThumbsOffset(track);
    if (prev) prev.disabled = offset <= 1;
    if (next) next.disabled = offset >= max - 1;
  };

  const applyColorFilter = (root) => {
    applyColorFilterInner(root);
    // Filtering hides/shows thumbs, changing the track width — re-clamp and
    // refresh arrows.
    setThumbsOffset(root, getThumbsOffset(root.querySelector("[data-pdp-thumbs]")));
  };

  const bindThumbsArrows = (root) => {
    const wrap = root.querySelector("[data-pdp-thumbs-wrap]");
    const track = root.querySelector("[data-pdp-thumbs]");
    const viewport = root.querySelector("[data-pdp-thumbs-viewport]");
    if (!wrap || !track || !viewport) return;

    if (wrap.dataset.thumbsArrowsBound !== "true") {
      wrap.dataset.thumbsArrowsBound = "true";

      wrap.addEventListener("click", (event) => {
        const arrow = event.target.closest("[data-thumbs-arrow]");
        if (!arrow || !wrap.contains(arrow)) return;
        // Page by a whole number of thumbs so the strip stays aligned.
        const first = track.querySelector(
          "[data-thumb]:not(.pdp__thumb--hidden)",
        );
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = first ? first.getBoundingClientRect().width + gap : 0;
        // +gap because the last thumb in view has no trailing gap.
        const perPage = step
          ? Math.max(1, Math.floor((viewport.clientWidth + gap) / step))
          : 1;
        const amount = step ? step * perPage : viewport.clientWidth;
        const dir = arrow.dataset.thumbsArrow === "next" ? 1 : -1;
        setThumbsOffset(root, getThumbsOffset(track) + dir * amount);
      });

      window.addEventListener("resize", () =>
        setThumbsOffset(root, getThumbsOffset(track)),
      );
    }

    setThumbsOffset(root, getThumbsOffset(track));
  };

  const hasColorSelected = (root) => {
    const picker = root.querySelector("variant-picker");
    return !!picker?.querySelector('.pdp__swatches input[type="radio"]:checked');
  };

  // Clears the color selection entirely: no swatch stays checked, and the label
  // falls back to its placeholder. The shopper must then actively pick a color.
  const deselectColor = (root) => {
    const picker = root.querySelector("variant-picker");
    if (!picker) return;
    picker
      .querySelectorAll('.pdp__swatches input[type="radio"]:checked')
      .forEach((input) => {
        input.checked = false;
        input.dataset.currentChecked = "false";
      });
    const colorLabel = root.querySelector("[data-color-label]");
    if (colorLabel) {
      colorLabel.textContent = colorLabel.dataset.placeholder || "—";
    }
  };

  const showColorRequired = (root, show) => {
    const msg = root.querySelector("[data-color-required]");
    if (msg) msg.hidden = !show;
    root
      .querySelector(".pdp__swatches")
      ?.classList.toggle("pdp__swatches--error", show);
  };

  // Keep Buy Now's hidden variant id + disabled state in sync with the ATC form.
  const syncBuyNow = (root) => {
    const mainId = root.querySelector(
      '.pdp__buy-form [name="id"], product-form-component [name="id"]',
    );
    const buyNowId = root.querySelector("[data-buy-now-variant]");
    const buyNowBtn = root.querySelector("[data-buy-now-btn]");
    const atcBtn = root.querySelector(".pdp__atc");

    if (mainId && buyNowId && mainId.value) {
      buyNowId.value = mainId.value;
    }
    if (buyNowBtn && atcBtn) {
      buyNowBtn.disabled = !!atcBtn.disabled;
    }
  };

  // Blocks add-to-cart / buy-now while no color is selected and prompts the shopper.
  const initColorRequirement = (root) => {
    if (root.dataset.colorRequireBound === "true") return;
    root.dataset.colorRequireBound = "true";

    root.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLElement) || form.tagName !== "FORM") return;

        // Always sync Buy Now variant id right before submit.
        if (form.matches("[data-buy-now-form]")) {
          syncBuyNow(root);
        }

        // Only guard products that actually have a color option.
        if (!root.querySelector(".pdp__swatches")) return;
        if (hasColorSelected(root)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        showColorRequired(root, true);
        root
          .querySelector(".pdp__swatches")
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      },
      true,
    );
  };

  const bindGalleryClear = (root) => {
    if (root.dataset.galleryClearBound === "true") return;
    root.dataset.galleryClearBound = "true";

    // Delegated so it survives the variant-picker morphing on variant change.
    root.addEventListener("click", (event) => {
      const clearBtn = event.target.closest("[data-gallery-clear]");
      if (clearBtn && root.contains(clearBtn)) {
        root.dataset.colorFilterActive = "false";
        deselectColor(root);
        applyColorFilter(root);
        return;
      }

      // Clicking a color swatch (re)activates filtering, even when the shopper
      // clicks the color that is already selected (which fires no change event).
      const swatch = event.target.closest(
        ".pdp__swatches label, .pdp__swatches input",
      );
      if (swatch && root.contains(swatch)) {
        root.dataset.colorFilterActive = "true";
        showColorRequired(root, false);
        requestAnimationFrame(() => applyColorFilter(root));
      }
    });
  };

  const bindGallery = (root) => {
    const thumbs = root.querySelector("[data-pdp-thumbs]");
    if (!thumbs) return;

    if (thumbs.dataset.thumbsBound === "true") return;
    thumbs.dataset.thumbsBound = "true";

    thumbs.addEventListener("click", (event) => {
      const thumb = event.target.closest("[data-thumb]");
      if (!thumb || !thumbs.contains(thumb)) return;
      selectThumb(root, thumb);
    });

    thumbs.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const thumb = event.target.closest("[data-thumb]");
      if (!thumb || !thumbs.contains(thumb)) return;
      event.preventDefault();
      selectThumb(root, thumb);
    });
  };

  const bindArrows = (root) => {
    const prevBtn = root.querySelector('[data-pdp-arrow="prev"]');
    const nextBtn = root.querySelector('[data-pdp-arrow="next"]');
    if (!prevBtn && !nextBtn) return;

    const getThumbButtons = () =>
      Array.from(root.querySelectorAll("[data-thumb]")).filter(
        (t) => !t.classList.contains("pdp__thumb--hidden"),
      );

    const getCurrentIndex = (thumbs) => {
      const idx = thumbs.findIndex((t) => t.classList.contains("is-active"));
      return idx >= 0 ? idx : 0;
    };

    const navigate = (direction) => {
      const thumbs = getThumbButtons();
      if (thumbs.length <= 1) return;
      const current = getCurrentIndex(thumbs);
      const next =
        direction === "next"
          ? (current + 1) % thumbs.length
          : (current - 1 + thumbs.length) % thumbs.length;
      selectThumb(root, thumbs[next]);
    };

    prevBtn?.addEventListener("click", () => navigate("prev"));
    nextBtn?.addEventListener("click", () => navigate("next"));

    const thumbs = getThumbButtons();
    if (thumbs.length <= 1) {
      prevBtn?.setAttribute("hidden", "");
      nextBtn?.setAttribute("hidden", "");
    }
  };

  const refreshGalleryFromHtml = (root, doc) => {
    const newGallery = doc.querySelector("[data-pdp-gallery]");
    const gallery = root.querySelector("[data-pdp-gallery]");
    if (!newGallery || !gallery) return false;

    gallery.innerHTML = newGallery.innerHTML;
    bindGallery(root);
    bindArrows(root);
    bindThumbsArrows(root);
    applyColorFilter(root);
    return true;
  };

  const handleVariantUpdate = (event) => {
    const root = event.target.closest("[data-pdp]");
    if (!root) return;

    const eventProductId = String(event.detail?.data?.productId || "");
    const rootProductId = String(root.dataset.productId || "");
    if (eventProductId && rootProductId && eventProductId !== rootProductId)
      return;

    // If the shopper cleared the color (Show all) while a variant section fetch
    // was still in flight, the variant-picker morph re-checks the swatch. Undo
    // that so the cleared state sticks.
    if (root.dataset.colorFilterActive === "false") {
      deselectColor(root);
    }

    const html = event.detail?.data?.html;
    if (html instanceof Document && refreshGalleryFromHtml(root, html)) return;

    const featured = event.detail?.resource?.featured_media;
    const preview = featured?.preview_image;
    if (preview?.src) {
      setMainImage(root, {
        src: preview.src,
        alt: featured.alt,
        mediaId: featured.id,
      });
      setActiveThumb(root, featured.id);
      setActiveThumbBySrc(root, preview.src);
    }

    applyColorFilter(root);
    syncBuyNow(root);
  };

  const initColorLabel = (root) => {
    const variantPicker = root.querySelector("variant-picker");
    if (!variantPicker) return;

    const colorLabel = root.querySelector("[data-color-label]");

    variantPicker.addEventListener("change", (event) => {
      const checked = variantPicker.querySelector(
        '.pdp__swatches input[type="radio"]:checked',
      );
      if (!checked) return;
      if (colorLabel) {
        const label = checked.closest("label");
        const hidden = label?.querySelector(".visually-hidden");
        if (hidden?.textContent)
          colorLabel.textContent = hidden.textContent.trim();
      }

      // Only a color change turns filtering on; changing size keeps the current
      // state (all images stay visible by default).
      const isColorChange = event.target?.closest?.(".pdp__swatches");
      if (isColorChange) {
        root.dataset.colorFilterActive = "true";
        showColorRequired(root, false);
      }

      // Filter the gallery immediately, without waiting for the section refetch.
      applyColorFilter(root);
    });
  };

  const initStickyAtc = (root) => {
    const trigger = root.querySelector("[data-atc-sticky-trigger]");
    const sticky = root.querySelector("[data-sticky-atc]");
    const mainBtn = root.querySelector(".pdp__atc");
    const stickyBtn = root.querySelector("[data-sticky-atc-btn]");

    if (!trigger || !sticky || !mainBtn) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show sticky if main ATC is above the fold or scrolled past
          const isVisible =
            !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle("is-visible", isVisible);
          sticky.setAttribute("aria-hidden", !isVisible);
        });
      },
      { threshold: 0 },
    );

    observer.observe(trigger);

    // Sync click
    stickyBtn?.addEventListener("click", () => {
      mainBtn.click();
      window.scrollTo({ top: trigger.offsetTop - 100, behavior: "smooth" });
    });
  };

  const initShare = (root) => {
    const shareBtn = root.querySelector(".pdp__share-btn");
    if (!shareBtn) return;

    shareBtn.addEventListener("click", () => {
      const url = shareBtn.dataset.shareUrl;
      const title = document.title;

      if (navigator.share) {
        navigator.share({ title, url }).catch(() => {});
      } else {
        navigator.clipboard.writeText(url).then(() => {
          const originalText = shareBtn.innerHTML;
          shareBtn.innerHTML =
            '<span class="material-symbols-outlined">check</span> Link copied!';
          setTimeout(() => {
            shareBtn.innerHTML = originalText;
          }, 2000);
        });
      }
    });
  };

  const initPdp = (root) => {
    if (!root) return;

    bindGallery(root);
    bindArrows(root);
    bindThumbsArrows(root);
    bindGalleryClear(root);
    initColorRequirement(root);
    initColorLabel(root);
    applyColorFilter(root);
    syncBuyNow(root);
    initStickyAtc(root);
    initShare(root);

    if (root.dataset.pdpInit === "true") return;
    root.dataset.pdpInit = "true";

    const section = root.closest(".shopify-section");
    section?.addEventListener(VARIANT_UPDATE, handleVariantUpdate);
  };

  const boot = () => {
    document.querySelectorAll("[data-pdp]").forEach(initPdp);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root =
      event.target?.querySelector?.("[data-pdp]") ||
      event.target?.closest?.("[data-pdp]");
    if (root) initPdp(root);
  });
})();
